<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="settings-container">
    <!-- Frame - Main container -->
    <div class="group-main-container">
      <!-- GroupAdmins component -->
      <div class="group-admins-wrapper">
        <GroupAdmins ref="groupAdminsRef" />
      </div>

      <!-- GroupAgentSelector component -->
      <div v-if="showAgentSelector" class="agent-selection-container">
        <h3 class="group-title">Agente asignado al grupo</h3>
        <div class="input-field">
          <label class="input-title">Agente del grupo</label>
          <CustomAutoComplete
            id="agent-select"
            v-model="selectedAgent"
            :options="availableAgents"
            option-value="userName"
            :option-label="getAgentLabel"
            placeholder="Buscar un agente..."
          />
          <span v-if="!selectedAgent" class="required-text"
            >Es necesario seleccionar un Agente para continuar</span
          >
        </div>
      </div>

      <!-- Cobranza section -->
      <div class="billing-selection-container">
        <h3 class="group-title billing-title">Cobranza</h3>
        <div class="input-field">
          <label class="input-title">Fecha de corte</label>
          <CustomSelect
            id="payment-limit-day"
            v-model="paymentLimitDay"
            :options="dayOptions"
            option-value="value"
            option-label="label"
            placeholder="Seleccione"
          />
        </div>
      </div>

      <!-- Core section -->
      <div class="billing-selection-container">
        <h3 class="group-title core-system-title">Core System</h3>
        <div class="input-field">
          <label class="input-title"
            >Al presionar el botón, se importaran todos los registros del grupo que actualmente
            están almacenados en Core System</label
          >
          <br />
          <div class="import-button-container">
            <CustomButton
              text="Importar desde Core"
              variant="primary"
              :icon-src="addUsersIcon"
              :show-icon="true"
              @click="handleImportDatabase"
            />
          </div>
        </div>
      </div>

      <!-- Save button container -->
      <div class="group-save-button-container">
        <CustomButton
          text="Guardar cambios"
          variant="secondary"
          :show-icon="true"
          :icon-src="saveIcon"
          icon-alt="Guardar cambios"
          :disabled="!hasValidData || isLoading"
          @click="handleSave"
        />
      </div>
    </div>

    <!-- Toast Message -->
    <ToastMessage
      v-model="showToast"
      :message="toastMessage"
      :submessage="toastSubmessage"
      :type="toastType"
      :icon="toastIcon"
      :duration="5000"
    />
  </div>
</template>

<script setup lang="ts" name="GroupSettings">
import { ref, computed, inject, onMounted, watch, nextTick } from 'vue'
import GroupAdmins from './GroupAdmins.vue'
import CustomButton from './CustomButton.vue'
import ToastMessage from './ToastMessage.vue'
import saveIcon from '@/assets/save-icon-white.svg'
import checkIcon from '@/assets/check-icon.svg'
import errorIcon from '@/assets/error-icon-white.svg'
import { ApiService } from '@/services/apiService'
import { useBulkLoadStore } from '@/stores/bulkLoad'
import type { AdministratorEmailDto, AgentDTO, MigrationResponseDto } from 'commons'
import CustomSelect from './CustomSelect.vue'
import CustomAutoComplete from './CustomAutoComplete.vue'
import addUsersIcon from '@/assets/add-users-black.svg'

interface Props {
  showAgentSelector?: boolean
  onAdministratorsUpdated?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  showAgentSelector: true,
})

const apiService = inject<ApiService>('apiService')
const bulkLoadStore = useBulkLoadStore()

if (!apiService) {
  throw new Error('apiService is not provided')
}

// GroupAdmins ref
const groupAdminsRef = ref()

// Agent selection state
const selectedAgent = ref('')
const availableAgents = ref<AgentDTO[]>([])
const showAgentError = ref(false)
const isLoading = ref(false)
const loadingText = ref('')
const businessId = computed(() => bulkLoadStore.selectedGroup?.id || '')

// Payment limit day state
const paymentLimitDay = ref<number | string>('')

// Generate day options from 1 to 31
const dayOptions = computed(() => {
  return Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1).padStart(2, '0'),
  }))
})

// Toast state
const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref(checkIcon)

// Change detection state
const originalAdminEmails = ref<string[]>([''])
const originalAdminNames = ref<string[]>([''])
const originalSelectedAgent = ref('')
const originalPaymentLimitDay = ref<number | string>('')
const hasChanges = ref(false)
const existingAdministrators = ref<Array<{ id: string; email: string; order: number }>>([]) // Store existing admins with IDs

// Helper function to show toast
const showToastMessage = (
  message: string,
  submessage: string = '',
  type: 'success' | 'error' = 'success',
  icon: string = checkIcon,
) => {
  toastMessage.value = message
  toastSubmessage.value = submessage
  toastType.value = type
  toastIcon.value = icon
  showToast.value = true
}

// Computed properties
const hasValidData = computed(() => {
  const hasValidNames = groupAdminsRef.value?.adminNames?.some((name: string) => name.trim() === '')
  const hasValidEmails = groupAdminsRef.value?.isEmailValid?.every((v: boolean) => v === true)
  // Agent validation is optional - button enables based on emails and changes only
  const hasValidAgent = true
  return !hasValidNames && hasValidEmails && hasValidAgent && hasChanges.value
})

// Change detection functions
const checkForChanges = () => {
  if (!groupAdminsRef.value) return false

  const currentAdminEmails = groupAdminsRef.value.adminEmails || ['']
  const currentAdminNames = groupAdminsRef.value.adminNames || ['']
  const adminNamesChanged =
    JSON.stringify(currentAdminNames) !== JSON.stringify(originalAdminNames.value)
  const adminEmailsChanged =
    JSON.stringify(currentAdminEmails) !== JSON.stringify(originalAdminEmails.value)
  const agentChanged = selectedAgent.value !== originalSelectedAgent.value
  const paymentLimitDayChanged = paymentLimitDay.value !== originalPaymentLimitDay.value

  hasChanges.value =
    adminNamesChanged || adminEmailsChanged || agentChanged || paymentLimitDayChanged
}

const updateOriginalValues = () => {
  if (groupAdminsRef.value) {
    originalAdminEmails.value = [...(groupAdminsRef.value.adminEmails || [''])]
    originalAdminNames.value = [...(groupAdminsRef.value.adminNames || [''])]
  }
  originalSelectedAgent.value = selectedAgent.value
  originalPaymentLimitDay.value = paymentLimitDay.value
  hasChanges.value = false
}

// Load existing administrators
const loadExistingAdministrators = async () => {
  try {
    isLoading.value = true
    const existingAdmins = bulkLoadStore.companyAdministrator

    // The API returns a single object with emails array
    if (existingAdmins.emails && existingAdmins.emails.length > 0) {
      // Store existing administrators with IDs
      existingAdministrators.value = existingAdmins.emails.map((admin: AdministratorEmailDto) => ({
        id: admin.id,
        email: admin.email,
        order: admin.order,
        name: admin.name,
      }))

      // Load existing emails into GroupAdmins component
      if (groupAdminsRef.value) {
        groupAdminsRef.value.adminNames = existingAdmins.emails.map(
          (admin: AdministratorEmailDto) => admin.name || '',
        )
        groupAdminsRef.value.adminEmails = existingAdmins.emails.map(
          (admin: AdministratorEmailDto) => admin.email,
        )
        groupAdminsRef.value.isEmailValid = new Array(groupAdminsRef.value.adminEmails.length).fill(
          true,
        )
      }
    }
  } catch (error) {
    console.error('Hubo un error: Error loading existing administrators ', error)
  } finally {
    isLoading.value = false
  }
}

/**
 * Validates form data before saving.
 *
 * @returns True if validation fails
 */
const validateBeforeSave = (): boolean => {
  if (!hasValidData.value) {
    if (props.showAgentSelector && !selectedAgent.value) {
      showAgentError.value = true
    }
    return true
  }
  return false
}

/**
 * Creates valid email objects from admin emails with IDs from existing administrators.
 *
 * @returns Array of valid email objects
 */
const createValidAdminEmails = () => {
  if (!groupAdminsRef.value) {
    return []
  }

  return groupAdminsRef.value.adminEmails
    .map((email: string, index: number) => {
      if (email.trim() === '') return null // Skip empty emails

      // Find if this email exists in existing administrators
      const existingAdmin = existingAdministrators.value.find((admin) => admin.email === email)

      return {
        ...(existingAdmin?.id && { id: existingAdmin.id }), // Include ID if exists
        order: index + 1,
        email: email,
        name: groupAdminsRef.value.adminNames[index] || '',
      }
    })
    .filter((email: unknown) => email !== null) // Remove null entries
}

/**
 * Shows success toast for administrator registrations.
 *
 * @param successfulRegistrations - Array of successful registrations
 */
const showAdminSuccessToast = (successfulRegistrations: unknown[]) => {
  if (successfulRegistrations.length > 0) {
    const successMessage =
      successfulRegistrations.length === 1
        ? `Administrador registrado exitosamente`
        : `${successfulRegistrations.length} administradores registrados exitosamente`
    console.log('✅ ADMIN SUCCESS:', successMessage)
    showToastMessage('Registro exitoso', successMessage, 'success', checkIcon)
  }
}

/**
 * Shows error toast for failed administrator registrations.
 *
 * @param failedRegistrations - Array of failed registrations
 */
const showAdminErrorToast = (
  failedRegistrations: Array<{ administratorEmail: { email: string }; detail: string }>,
) => {
  if (failedRegistrations.length > 0) {
    const errorMessage =
      failedRegistrations.length === 1
        ? `Error al registrar administrador`
        : `${failedRegistrations.length} administradores tuvieron errores al registrarse`
    const errorSubmessage =
      failedRegistrations.length === 1
        ? `${failedRegistrations[0].administratorEmail.email} - ${failedRegistrations[0].detail}`
        : ''
    console.log('❌ ADMIN ERROR:', errorMessage)
    showToastMessage(errorMessage, errorSubmessage, 'error', errorIcon)
  }
}

/**
 * Saves administrators and handles the response.
 */
const saveAdministrators = async () => {
  if (!groupAdminsRef.value) {
    return
  }

  const validEmails = createValidAdminEmails()

  const adminResult = await apiService.addCompanyAdministrators({
    businessId: businessId.value,
    emails: validEmails,
  })

  // Process admin results
  const successfulRegistrations = adminResult.filter((admin: { success: boolean }) => admin.success)
  const failedRegistrations = adminResult.filter((admin: { success: boolean }) => !admin.success)

  showAdminSuccessToast(successfulRegistrations)
  showAdminErrorToast(failedRegistrations)

  // Refresh administrators in GroupAdmins component
  await groupAdminsRef.value.refreshAdministrators()

  // Also refresh existing administrators data in Settings
  await loadExistingAdministrators()

  // Notify parent component to refresh summary pills
  if (props.onAdministratorsUpdated) {
    props.onAdministratorsUpdated()
  }
}

/**
 * Saves the selected agent.
 */
const saveAgent = async () => {
  if (props.showAgentSelector && selectedAgent.value) {
    await apiService.updateAgentGroup(bulkLoadStore.selectedGroup?.id, selectedAgent.value)
    showToast.value = true
    toastMessage.value = 'Configuración guardada'
    toastSubmessage.value = 'Se modificó exitosamente la configuración'
    toastType.value = 'success'
    toastIcon.value = checkIcon
    bulkLoadStore.setSelectedAgent(selectedAgent.value)
  }
}

/**
 * Saves the payment limit day.
 */
const savePaymentLimitDay = async () => {
  if (paymentLimitDay.value && paymentLimitDay.value !== '') {
    const paymentLimitDayValue = Number(paymentLimitDay.value)
    if (paymentLimitDayValue >= 1 && paymentLimitDayValue <= 31) {
      await apiService.updatePaymentLimitDay(businessId.value, paymentLimitDayValue)
    }
  }
}

// Save function
const handleSave = async () => {
  if (validateBeforeSave()) {
    return
  }

  isLoading.value = true
  try {
    await saveAdministrators()
    await saveAgent()
    await savePaymentLimitDay()

    // Reset changes after successful save
    updateOriginalValues()
  } catch (error) {
    console.error('Hubo un error: Error saving settings ', error)
    showToastMessage('Error al guardar la configuración', '', 'error', errorIcon)
  } finally {
    isLoading.value = false
  }
}

const handleImportDatabase = async () => {
  if (!bulkLoadStore.selectedGroup?.id) {
    console.error('Hubo un error: No group selected')
    return
  }

  isLoading.value = true
  loadingText.value = 'Importando datos...'
  try {
    const groupId = Number(bulkLoadStore.selectedGroup.id)
    const result = await apiService.migrateLegacyGroups([groupId])

    if (result && result.summary) {
      analyzeImportResponse(result)
    }
  } catch {
    showImportError()
  } finally {
    isLoading.value = false
  }
}

const analyzeImportResponse = (response: MigrationResponseDto) => {
  const { employeesCreated, employeesUpdated, employeesSkipped } = response.summary

  showToast.value = true
  toastMessage.value = 'Importación exitosa'

  if (employeesCreated > 0 && employeesUpdated > 0) {
    toastSubmessage.value = `Se importaron ${employeesCreated} registros y se actualizaron ${employeesUpdated}.`
  } else if (employeesCreated > 0) {
    toastSubmessage.value = `Se importaron ${employeesCreated} registros exitosamente.`
  } else if (employeesUpdated > 0) {
    toastSubmessage.value = `Se actualizaron ${employeesUpdated} registros.`
  } else {
    toastSubmessage.value = `No se encontraron registros nuevos para importar.`
    console.log(
      `No se encontraron registros nuevos para importar. \n${employeesSkipped} registros ya existían - no se actualizan`,
    )
  }

  toastType.value = 'success'
  toastIcon.value = checkIcon
}

const showImportError = () => {
  showToast.value = true
  toastMessage.value = 'Error en la importación'
  toastSubmessage.value = 'Hubo un error durante la importación de datos'
  toastType.value = 'error'
  toastIcon.value = errorIcon
}

// Load existing data when component mounts
onMounted(async () => {
  if (props.showAgentSelector) {
    availableAgents.value = bulkLoadStore.availableAgents || []
    selectedAgent.value = bulkLoadStore.selectedAgent || ''
  }

  // Load payment limit day from company data
  if (businessId.value) {
    try {
      const company = await apiService.getCompanyByBusinessId(businessId.value)
      // Set paymentLimitDay if it's a valid number (1-31), otherwise leave empty to show "Seleccione"
      if (
        company.paymentLimitDay !== null &&
        company.paymentLimitDay !== undefined &&
        company.paymentLimitDay !== 0 &&
        company.paymentLimitDay >= 1 &&
        company.paymentLimitDay <= 31
      ) {
        paymentLimitDay.value = company.paymentLimitDay
      } else {
        paymentLimitDay.value = ''
      }
    } catch (error) {
      console.error('Hubo un error: Error loading payment limit day ', error)
      paymentLimitDay.value = ''
    }
  }

  // Load existing administrators
  await loadExistingAdministrators()

  // Wait for GroupAdmins to be ready and set initial values
  await nextTick()
  if (groupAdminsRef.value) {
    updateOriginalValues()
  }
})

// Watch for changes in payment limit day
watch(paymentLimitDay, () => {
  checkForChanges()
})

watch(
  () => groupAdminsRef.value?.adminNames,
  () => {
    checkForChanges()
  },
  { deep: true },
)

// Watch for changes in GroupAdmins component
watch(
  () => groupAdminsRef.value?.adminEmails,
  () => {
    checkForChanges()
  },
  { deep: true },
)

// Watch for changes in selected agent
watch(selectedAgent, () => {
  checkForChanges()
})

const getAgentLabel = (agent: AgentDTO) => {
  return `${agent.nombre} ${agent.apPaterno} ${agent.apMaterno}`
}
</script>

<style scoped>
/* Settings container */
.settings-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 0px 0px 0px; /* Further reduced top padding for separation from tabs */
  gap: 0px; /* Remove gap between external container and white card */
  width: 100%;
  height: auto;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
}

/* Override group-main-container padding to ensure uniform spacing */
.settings-container .group-main-container {
  padding-top: 0px !important;
  gap: 0px !important;
}

/* Test button container */
.agent {
  display: flex;
  justify-content: center;
  width: 100%;
  order: 1; /* Ensure it comes after GroupAdmins */
}

/* Override group-card min-height for agent selector */
.group-card {
  min-height: 200px; /* Reduced height for agent selector only */
}

/* GroupAdmins wrapper - first section should have same spacing as others */
.group-admins-wrapper {
  width: 100%;
  margin-top: 24px;
  margin-bottom: 0px;
  display: flex;
  flex-direction: column;
}

/* Override GroupAdmins container padding to reduce space - remove all padding-top */
.group-admins-wrapper :deep(.group-container) {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  padding-left: 0px !important;
  padding-right: 0px !important;
  margin: 0px !important;
  gap: 0px !important;
}

/* Override GroupAdmins main container to remove extra padding */
.group-admins-wrapper :deep(.group-main-container) {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  padding-left: 40px !important;
  padding-right: 40px !important;
  margin: 0px !important;
  gap: 0px !important;
}

/* Ensure the card inside GroupAdmins has no extra margin */
.group-admins-wrapper :deep(.group-card) {
  margin: 0px !important;
}

/* Agent selection container */
.agent-selection-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 32px;
  gap: 32px;
  width: 900px;
  height: auto;
  flex: none;
  order: 1 !important;
  flex-grow: 0;
  z-index: 0;
  min-height: 200px;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 24px;
  margin-top: 24px;
}

/* Billing selection container */
.billing-selection-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 32px;
  gap: 32px;
  width: 900px;
  height: auto;
  flex: none;
  order: 2 !important;
  flex-grow: 0;
  z-index: 0;
  min-height: auto;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 24px;
  margin-top: 24px;
}

/* Remove any extra spacing from the title in billing container */
.billing-selection-container .billing-title,
.billing-selection-container h3.group-title {
  margin: 0 !important;
  margin-left: 10px !important;
  padding: 0 !important;
  line-height: 1.2 !important;
  height: auto !important;
  width: auto !important;
  display: block !important;
}

/* Agent label */
.agent-label {
  width: 100%;
  height: 20px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 140%;
  color: #333333;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

/* Agent select wrapper */
.agent-select-wrapper {
  position: relative;
  width: 100%;
  height: 78px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
}

/* Agent select */
.agent-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 100%;
  height: 48px;
  padding: 12px 50px 12px 16px;
  border: 1px solid #d5d5d5;
  border-radius: 16px;
  background: #ffffff;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 140%;
  color: #333333;
  outline: none;
  transition: border-color 0.2s;
  cursor: pointer;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

.agent-select:focus {
  border-color: #003896;
}

.agent-select:disabled {
  background: #f2f2f2;
  color: #a1a1a1;
  cursor: not-allowed;
}

/* Select indicators */
.select-indicators {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
}

.select-line {
  width: 2px;
  height: 24px;
  background: #bdbdbd;
}

.select-arrow {
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid #888;
}

/* Error message */
.error-message {
  width: 100%;
  height: auto;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: #d32f2f;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
}

/* Save button container */
.group-save-button-container {
  display: flex !important;
  flex-direction: row !important;
  justify-content: flex-end !important;
  align-items: center !important;
  width: 900px !important;
  margin-top: 24px !important;
  margin-left: auto !important;
  margin-right: 0 !important;
  padding: 0px !important;
  gap: 0px !important;
  height: auto !important;
  flex: none !important;
  order: 3 !important;
  align-self: flex-end !important;
  flex-grow: 0 !important;
}

/* Override CustomButton styles for save button */
:deep(.custom-button.secondary) {
  width: 280px;
  height: 48px;
  background: #003896;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 30px;
}

.input-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 1px;
  isolation: isolate;
  width: 100%;
  min-height: 80px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 1;
}

.input-title {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
  height: 22px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 1;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
}

.import-button-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: 16px;
}
</style>
